import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { MetricCard } from "@/components/admin/MetricCard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  RefreshCw,
  Users,
  Activity,
  Clock,
  FileText,
  Mic,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  useUserActivity,
  useActivitySummary,
  useActivityTrend,
} from "@/hooks/use-admin-user-activity";

const STATUS_FILTERS = [
  { value: "_all", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "trial", label: "Trial" },
  { value: "pending_approval", label: "Pending Approval" },
  { value: "suspended", label: "Suspended" },
];

function getStatusBadge(status: string) {
  switch (status) {
    case "active":
      return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">Active</Badge>;
    case "trial":
      return <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">Trial</Badge>;
    case "pending_approval":
      return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">Pending</Badge>;
    case "suspended":
      return <Badge variant="destructive">Suspended</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

export default function UserActivity() {
  // State
  const [statusFilter, setStatusFilter] = useState("_all");

  // Hooks
  const { users, total, page, pageSize, loading: usersLoading, fetch: fetchUsers } = useUserActivity();
  const { summary, loading: summaryLoading, fetch: fetchSummary } = useActivitySummary();
  const { trend, loading: trendLoading, fetch: fetchTrend } = useActivityTrend();

  // Initial fetch
  useEffect(() => {
    fetchSummary();
    fetchTrend(30);
    fetchUsers({ status: statusFilter !== "_all" ? statusFilter : undefined });
  }, []);

  // Refetch users on filter change
  useEffect(() => {
    fetchUsers({ status: statusFilter !== "_all" ? statusFilter : undefined }, 1);
  }, [statusFilter]);

  // Handle refresh
  const handleRefresh = () => {
    fetchSummary();
    fetchTrend(30);
    fetchUsers({ status: statusFilter !== "_all" ? statusFilter : undefined }, page);
  };

  // Handle pagination
  const handlePrevPage = () => {
    if (page > 1) {
      fetchUsers({ status: statusFilter || undefined }, page - 1);
    }
  };

  const handleNextPage = () => {
    if (page * pageSize < total) {
      fetchUsers({ status: statusFilter || undefined }, page + 1);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  // Format trend data for chart
  const chartData = trend.map(point => ({
    ...point,
    date: format(new Date(point.date), "MMM d"),
  }));

  return (
    <AdminLayout
      title="User Activity"
      description="Monitor user engagement and usage metrics"
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <MetricCard
          title="Daily Active Users"
          value={summary?.dau ?? 0}
          icon={Users}
          loading={summaryLoading}
        />
        <MetricCard
          title="Weekly Active Users"
          value={summary?.wau ?? 0}
          icon={Activity}
          loading={summaryLoading}
        />
        <MetricCard
          title="Monthly Active Users"
          value={summary?.mau ?? 0}
          icon={TrendingUp}
          loading={summaryLoading}
        />
        <MetricCard
          title="Reports (7d)"
          value={summary?.total_reports_7d ?? 0}
          icon={FileText}
          loading={summaryLoading}
          description={
            summary?.avg_reports_per_user
              ? `${summary.avg_reports_per_user.toFixed(1)} avg/user`
              : undefined
          }
        />
      </div>

      {/* Activity Trend Chart */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Activity Trend (30 days)
          </CardTitle>
          <CardDescription>
            Daily active users and report generation volume
          </CardDescription>
        </CardHeader>
        <CardContent>
          {trendLoading ? (
            <Skeleton className="h-[250px] w-full" />
          ) : chartData.length === 0 ? (
            <div className="h-[250px] flex items-center justify-center text-muted-foreground">
              No activity data available
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  className="text-xs"
                  tick={{ fill: "hsl(var(--muted-foreground))" }}
                />
                <YAxis
                  yAxisId="left"
                  className="text-xs"
                  tick={{ fill: "hsl(var(--muted-foreground))" }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  className="text-xs"
                  tick={{ fill: "hsl(var(--muted-foreground))" }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "6px",
                  }}
                />
                <Legend />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="active_users"
                  stroke="#3b82f6"
                  fill="#3b82f680"
                  name="Active Users"
                />
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="reports"
                  stroke="#22c55e"
                  fill="#22c55e80"
                  name="Reports"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((status) => (
                <SelectItem key={status.value} value={status.value}>
                  {status.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={usersLoading || summaryLoading || trendLoading}
        >
          <RefreshCw
            className={cn(
              "h-4 w-4 mr-2",
              (usersLoading || summaryLoading || trendLoading) && "animate-spin"
            )}
          />
          Refresh
        </Button>
      </div>

      {/* User Activity Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            User Activity
            <Badge variant="secondary" className="ml-2">
              {total}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Reports</TableHead>
                <TableHead>Dictation</TableHead>
                <TableHead>Last Active</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usersLoading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <TableRow key={idx}>
                    <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  </TableRow>
                ))
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {user.full_name || "Unknown"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {user.email || user.user_id.slice(0, 8) + "..."}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(user.status)}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3 text-muted-foreground" />
                        {user.reports_count}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1">
                        <Mic className="h-3 w-3 text-muted-foreground" />
                        {user.dictation_minutes} min
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {user.last_active ? (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDistanceToNow(new Date(user.last_active), {
                            addSuffix: true,
                          })}
                        </span>
                      ) : (
                        "Never"
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {!usersLoading && total > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {(page - 1) * pageSize + 1} -{" "}
                {Math.min(page * pageSize, total)} of {total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
